// @flow
import Cookies from "js-cookie"
import * as R from "ramda"
import * as cookies from "@kiwicom/nitro/lib/services/session/cookies"
import { USER_ID, AFFILIATE_ID } from "@kiwicom/nitro/lib/consts/cookies"
import type { EventPayload, Event, Props } from "@kiwicom/nitro/lib/records/Event"
import * as logger from "@kiwicom/nitro/lib/services/log/logger"

import session from "common/browser/session"
import { getABVersions } from "common/services/abTesting/abTesting"
import { get, getOr } from "common/utils/get"
import has from "common/utils/has"
import * as perfTracker from "common/utils/perfTracker"
import promiseRetry, { AbortError } from "common/services/fetch/promiseRetry"
import { getBidFromUrl } from "common/services/url/getBidFromUrl"
import { cuckooEvents } from "common/consts/enums"
import constants from "../../consts/config"
import * as utm from "../../../../app/scripts/tools/utm"
import * as gwm from "../../../../app/scripts/tools/gwm"
import { getLanguageInfo } from "../staticData/index"
import type { LanguageInfo } from "../../records/LanguageInfo"
import type { IframeEvent } from "../../types/iframeEvents"
import parseCookie from "../utils/parseCookie"
import type { CuckooEvent } from "../../consts/enums"
import { KW_AUTH_TOKEN, UA_SESSION_TOKEN } from "../../consts/auth"

type Level = "critical" | "error" | "warning" | "track" | "log" | "info"
export type EventProps = { [string]: any } | void

type CuckooTrackOptions = {
  disableLoggly?: boolean,
}

function isLoggedIn() {
  return Boolean(Cookies.get(UA_SESSION_TOKEN) || Cookies.get(KW_AUTH_TOKEN))
}

function getBid(): ?number {
  return window.SP_GLOBALS.SKYPICKER_MODULE === "mmb"
    ? Number(getBidFromUrl(window.location.pathname))
    : undefined
}

function getTrackName(event: CuckooEvent): string {
  if (event.legacyName) {
    return event.legacyName
  }

  return [event.category, event.subCategory, event.action, event.detail].filter(Boolean).join(": ")
}

// Can be updated when the user logs in
const getAffilID = (): string => cookies.load(AFFILIATE_ID) || ""

const getState = () => {
  try {
    return window.reduxStore.getState()
  } catch (error) {
    return {}
  }
}

/**
 * Inspired by https://segment.com/docs/libraries/analytics.js/#track
 * @param event eg. "Booking finished", supports » to divide "category » action"
 * @param properties e.g. { logstash: {...}, ga: {label: "...", value: "..."} }
 */
class Cuckoo {
  userId: ?string

  fake: boolean

  iframeTrackingLoaded: boolean

  enabled: boolean

  deeplinkId: string

  sessionId: string

  pageViewId: string

  affilId: string

  affilParams: { [key: string]: string }

  UTMs: { [key: string]: string }

  pageName: string

  module: string

  abVersion: ?string

  host: string

  brandingId: string

  langInfo: LanguageInfo

  langId: string

  USE_XHR: boolean

  simpleABVersions: { [string]: string }

  bid: ?number

  constructor() {
    // will sent all logs to console
    this.fake = false

    if (typeof window !== "undefined" && window.SP_GLOBALS) {
      this.enabled = true
      this.fake = Cookies.get("debug_fakeCuckoo") === "true"
      this.deeplinkId = session.deeplinkId || ""
      this.sessionId = session.sessionId
      this.pageViewId = session.pageViewId
      this.affilId = getAffilID()
      this.affilParams = session.affiliate?.params || {}
      this.UTMs = session.UTMs
      this.pageName = window.SP_GLOBALS.SKYPICKER_PAGE_NAME || window.SP_GLOBALS.SKYPICKER_PAGE
      this.module = window.SP_GLOBALS.SKYPICKER_MODULE
      this.abVersion = window.SP_GLOBALS.RELEASE_INFO.ab_version
      this.host = window.location.host
      this.brandingId = window.__INITIAL_STATE__.options.branding.id
      this.langInfo = getLanguageInfo(window.__INITIAL_STATE__)
      this.iframeTrackingLoaded = false
      this.bid = getBid()

      logger.statics.project = "frontend/frontend"
      logger.statics.module = this.module
      logger.statics.pageName = this.pageName
      logger.statics.langId = this.langInfo.id
      logger.statics.pageViewId = this.pageViewId
      logger.statics.brandingId = this.brandingId
      logger.statics.affilParams = this.affilParams
      logger.statics.bid = Number(this.bid || 0)
      logger.statics.splitster = getOr({}, ["__INITIAL_STATE__", "splitster"], window)
      logger.statics.UTMs = this.UTMs
      logger.settings.api = {
        devUrl: "https://logg.kiwi.com/logg/loglady-raw-dev",
      }
    } else {
      // Disable on server
      this.enabled = false
    }

    // http://hacks.mozilla.org/2009/07/cross-site-xmlhttprequest-with-cors/ &
    // https://developer.mozilla.org/en-US/docs/DOM/XMLHttpRequest#withCredentials
    this.USE_XHR =
      typeof window !== "undefined" &&
      window.XMLHttpRequest &&
      "withCredentials" in new XMLHttpRequest()

    // SetUp Infinario Timezone
    if (typeof window !== "undefined" && has("infinario", window)) {
      const timezone = -new Date().getTimezoneOffset() / 60
      window.infinario.update({ timezone_vs_UTC: timezone })
    }

    // listen for iframeTracking load event
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("message", this.receiveIframeMessage, false)
    }
  }

  setBid = (bid: number) => {
    this.bid = bid
    logger.statics.bid = bid
  }

  // export getTrackName function in the cuckoo object
  getTrackName = getTrackName

  // Control
  // ===
  disable() {
    this.enabled = false
  }

  enable() {
    this.enabled = true
  }

  /**
   * Tracking iframe sends a message when it is loaded, so we can enable tracking
   */
  receiveIframeMessage = (event: MessageEvent) => {
    if (event.origin === "https://www.kiwi.com" && event.data === "load") {
      this.iframeTrackingLoaded = true
    }
  }

  identify(userId: string) {
    this.userId = userId
    if (this.enabled) {
      const segments = {
        lang: window.SP_GLOBALS.SKYPICKER_LNG,
        country: window.SP_GLOBALS.SKYPICKER_COUNTRY,
        // affiliate, //FIXME: solve, how to get these. We use them in other getABVersions calls
        // userAgentParsed,
      }
      this.simpleABVersions = getABVersions(
        this.userId,
        (cookieName: string) => Cookies.get(cookieName) || "",
        param => window.GET_PARAMETER_BY_NAME(param),
        segments,
      )
    }
  }

  maybeSend(
    level: Level,
    ev: CuckooEvent | string,
    props: EventProps,
    options: CuckooTrackOptions = {},
    callback: ?() => void = null,
  ) {
    if (this.enabled) {
      let evString

      if (typeof ev === "object") {
        evString = this.getTrackName(ev)
        if (ev._last) {
          if (typeof ev.limit === "number") {
            const timeFromLast = +new Date() - ev._last
            if (timeFromLast < ev.limit * 1000) {
              return // Do not run
            }
          } else if (ev.limit === "once") {
            return // Do not run again
          }
        }
        ev._last = +new Date() // eslint-disable-line no-param-reassign
      } else {
        evString = ev
      }

      // Avoid circular references errors in Error object
      // Fix of incompatibility in other browsers than Safari (err objects in logs)
      if (props && get("err", props) && props.err instanceof Error) {
        /* eslint-disable no-param-reassign */
        props.err = {
          message: get(["err", "message"], props),
          stack: get(["err", "stack"], props),
        }
        /* eslint-enable no-param-reassign */
      }
      if (typeof ev === "object") {
        // track logLady only for enums style tracking, ignore track middleware
        // $FlowExpected: bad typings
        this.trackLogLady(level, ev, props || {}, callback)
      }
      switch (level) {
        case "info":
          this.printToConsole("logstash", evString, props)
          break
        case "track":
        case "log":
          if (props) {
            this.gtm(evString, props)
          }
          this.send(evString, props, options)
          break
        case "warning":
          this.send(`[warning] ${evString}`, props, options)
          break
        case "error":
          this.send(`[error] ${evString}`, props, options)
          break
        case "critical":
          this.send(`[critical] ${evString}`, props, options)
          break
        default:
          throw new Error(`Cuckoo unsupported log level: ${level}`)
      }
    }
  }

  trackLogLady(level: Level, event: Event, data: { [string]: any }, callback: ?() => void = null) {
    logger.log(event, data).then(callback)
  }

  // Decorators for tracking various log levels
  // ===
  info(ev: CuckooEvent, props: EventProps) {
    this.maybeSend("info", ev, props)
  }

  log(ev: CuckooEvent, props: EventProps) {
    this.maybeSend("log", ev, props)
  }

  track(ev: CuckooEvent | string, props: EventProps, options: CuckooTrackOptions = {}) {
    this.maybeSend("track", ev, props, options)
  }

  promiseTrack = (
    ev: CuckooEvent | string,
    props: EventProps,
    options: CuckooTrackOptions = {},
    timeout: number = 2000,
  ): Promise<void> =>
    new Promise(resolve => {
      setTimeout(resolve, timeout)
      this.maybeSend("track", ev, props, options, resolve)
    })

  warning(ev: CuckooEvent, props: EventProps, options: CuckooTrackOptions = {}) {
    this.maybeSend("warning", ev, props, options)
  }

  error(ev: CuckooEvent, props: EventProps) {
    this.maybeSend("error", ev, props)
  }

  critical(ev: CuckooEvent, props: EventProps, options: CuckooTrackOptions = {}) {
    this.maybeSend("critical", ev, props, options)
  }

  nitro(ev: EventPayload, props: Props) {
    // $FlowExpected: _last is BS
    this.maybeSend("track", ev, props)
  }

  ga(category: string, action: string, label: ?string, value: ?string) {
    if (this.enabled && !this.fake && window.ga) {
      window.ga("send", "event", category, action, label, value)
    } else {
      this.printToConsole(
        "ga",
        `category: ${category}, action: ${action}, label: ${label || ""}, value: ${value || ""}`,
      )
    }
  }

  gaIdentify(userId: string) {
    if (this.enabled && !this.fake && window.ga) {
      window.ga("set", "userId", userId)
    }
    if (this.enabled) {
      const segments = {
        lang: window.SP_GLOBALS.SKYPICKER_LNG,
        country: window.SP_GLOBALS.SKYPICKER_COUNTRY,
        // affiliate, //FIXME: solve, how to get these. We use them in other getABVersions calls
        // userAgentParsed,
      }
      this.simpleABVersions = getABVersions(
        this.userId,
        (cookieName: string) => Cookies.get(cookieName) || "",
        param => window.GET_PARAMETER_BY_NAME(param),
        segments,
      )
    }
  }

  gaPageView() {
    if (this.enabled && !this.fake && window.ga) {
      window.ga("send", "pageview")
    } else {
      this.printToConsole("ga", "pageview")
    }
  }

  // eslint-disable-next-line flowtype/no-weak-types
  gtm(event: string, payload: Object) {
    new Promise((resolve, reject) => {
      const timeFromPageLoad = perfTracker.getTimeFromPageLoad()

      if (this.enabled && !this.fake && window.dataLayer && !window.SP_GLOBALS.IS_STAGING) {
        setTimeout(reject, 1000)
        window.dataLayer.push({
          event,
          affilId: getAffilID(),
          ...payload,
          timeFromPageLoad,
          eventCallback: resolve,
        })
      } else {
        this.printToConsole("gtm", event, { ...payload, timeFromPageLoad })
        resolve()
      }
    }).catch(error => {
      this.warning(cuckooEvents.GENERAL_TRACKING_FAILED, {
        service: "DataLayer",
        error,
      })
    })
  }

  // eslint-disable-next-line flowtype/no-weak-types
  iframeTracking(event: string, payload: Object = {}) {
    if (!this.enabled) {
      return
    }

    // defined inline to close over the event variable
    const sendEventToTrackingIframe = () => {
      // flow can't parse querySelector so we have to cast the exact type
      const trackingIframe = ((document.querySelector("#trackingIframe"): any): HTMLIFrameElement)
      const iframeAvailable = trackingIframe && trackingIframe.contentWindow
      if (iframeAvailable && this.iframeTrackingLoaded) {
        try {
          const pageUrl = window.location.href
          const iframeSrc = `${trackingIframe.src.split("#")[0]}#${pageUrl}`
          trackingIframe.setAttribute("src", iframeSrc)
          // metadata coming from cookies, local storage and cuckoo
          const utmParams = utm.loadFromSession(this)
          const affilId = Cookies.get("SKYPICKER_AFFILIATE") || ""
          const sub1 = Cookies.get("sub1Param") || ""
          const cuckooMetadata = R.pick(
            ["userId", "sessionId", "pageName", "module", "userId", "pageViewId"],
            this,
          )

          // get metadata from store
          const state = window.reduxStore.getState()
          const { country } = state.options
          const { language } = state.options
          const email = state.user.getIn(["user", "email"])
          // remove keys with value "Invalid Date" returned by date-fns.format
          const validatedPayload = R.pickBy(value => value !== "Invalid Date", payload)
          const trackingEvent: IframeEvent = {
            name: event,
            payload: {
              affilId,
              sub1,
              country,
              language,
              email,
              pageUrl,
              ...cuckooMetadata,
              ...utmParams,
              ...validatedPayload,
            },
            devMode: this.fake,
          }
          trackingIframe.contentWindow.postMessage(JSON.stringify(trackingEvent), "*")
        } catch (error) {
          this.printToConsole("iframeTracking error", error)
          // promiseRetry aborts retries if AbortError is thrown
          throw new AbortError(error)
        }
        return Promise.resolve()
      }
      // promiseRetry will swallow this, does a retry if an error is thrown
      throw new Error("iframe not loaded")
    }

    // try sending the event to iframe with exponential backoff
    promiseRetry(sendEventToTrackingIframe, {
      retries: 4,
    }).catch(error => this.printToConsole("iframeTracking error", error))
  }

  infinario(event: string, data: any = {}, includeSplitster?: boolean = false) {
    if (this.enabled && !this.fake && window.infinario) {
      try {
        if (event === "identify") {
          window.infinario.identify({
            email_id: data.toLowerCase().trim(),
            user_id: cookies.load(USER_ID) || "",
          })
          window.infinario.update({ email: data.toLowerCase().trim() })
        } else if (event === "Page loaded") {
          window.infinario.update({
            language: this.langInfo.hreflang.split("-")[0],
            languageISO: this.langInfo.hreflang,
          })
        } else {
          if (has("data.email", data)) {
            data.email = data.email.toLowerCase() // eslint-disable-line no-param-reassign
          }

          const payload = {
            ...data,
            ab_version: window.SP_GLOBALS.RELEASE_INFO.ab_version,
            affilId: getAffilID(),
            lang: this.langInfo.hreflang, // hreflang from spreadsheet (because it will be clear in ISO 639 format)
            pageName: this.pageName,
            isStaging: window.SP_GLOBALS.IS_STAGING,
            isLoggedIn: isLoggedIn(),
            user_id: cookies.load(USER_ID) || "",
            module: this.module,
            bid: this.bid,
            email_id:
              window.reduxStore && R.toLower(getOr("", ["user", "user", "email"], getState())),
            cookiePolice: parseCookie(Cookies.get("cookies_settings")),
            ...utm.loadFromSession(this),
            ...gwm.loadFromSession(),
            ...(includeSplitster ? getOr({}, ["__INITIAL_STATE__", "splitster"], window) : {}),
          }
          window.infinario.track(event, payload)

          if (event === "subscription") {
            window.infinario.update({ email: data.email.toLowerCase().trim() })

            // we want to subscribe for notifications as well
            if (window.infinario.notifications.isAvailable()) {
              window.infinario.notifications.isSubscribed(
                (subscriptionActive, notificationsDenied) => {
                  if (!subscriptionActive && !notificationsDenied)
                    window.infinario.notifications.subscribe()
                },
              )
            }
          }
        }
      } catch (err) {
        console.error(err)
      }
    } else {
      this.printToConsole("infinario", event, data)
    }
  }

  screenRecording(event: string, data: any) {
    if (this.enabled && !this.fake) {
      if (window.FS) {
        // stringified data, type must be suffix of the name
        // https://help.fullstory.com/develop-js/setuservars
        window.FS.setUserVars({ [`${event}_str`]: data })
      }
      if (!window.FS) {
        this.printToConsole("screen recording attributes sending failed", event, data)
      }
    }
  }

  send(ev: string, raw: EventProps = {}, options: CuckooTrackOptions = {}) {
    const addedProps = R.pickBy(Boolean, {
      "@session": this.sessionId,
      "@deeplinkId": this.deeplinkId,
      "@timestamp": new Date().toISOString(),
      "@pageViewId": this.pageViewId,
      "@pageName": this.pageName,
      "@module": this.module,
      "@abVersion": this.abVersion,
      "@host": this.host,
      "@brand": this.brandingId,
      "@simpleABVersions": this.simpleABVersions,
      "@bid": this.bid,
    })

    const props = { ...raw, ...addedProps }

    if (typeof window !== "undefined") {
      // TODO: make it work for Node
      if (this.fake) {
        this.printToConsole("track", ev, props)
      } else {
        // Loggly
        if (this.simpleABVersions.AB17 === "Loggly" && !options.disableLoggly) {
          if (typeof window !== "undefined" && window._LTracker) {
            window._LTracker.push({
              userId: this.userId,
              event: ev,
              properties: R.omit(["@simpleABVersions"], props),
            })
          }
        }

        // Our logging
        if (this.USE_XHR) {
          const req = new XMLHttpRequest()
          if (!__DEV__) {
            req.onreadystatechange = () => {
              // 4 means request is in done state
              if (req.readyState === 4 && req.status !== 200) {
                window.Raven.captureMessage("Logmole request failed", {
                  level: "info",
                  extra: {
                    status: req.status,
                    response: req.response,
                  },
                })
              }
            }
          }
          req.open("POST", `${constants.logstashApiUrl}`, true)
          req.withCredentials = false // withCredentials cannot be modified until after calling .open on Android and Mobile Safari
          req.setRequestHeader("Content-type", "text/plain") // Actually JSON, but need to be set to plain to avoid OPTIONS request
          req.send(
            JSON.stringify({
              userId: this.userId,
              event: ev,
              properties: props,
              source: "frontend",
            }),
          )
        } else {
          // TODO: Via inserting script
        }
      }
    }
  }

  printToConsole(target: string = "cuckoo", ev: string, props?: EventProps) {
    let color
    switch (target) {
      case "infinario":
        color = "#8763FE"
        break
      case "track":
        color = "#106FC4"
        break
      case "ga":
        color = "#EE5A30"
        break
      case "gtm":
        color = "#E683EC"
        break
      default:
        color = "#38425D"
    }

    if (Cookies.get("showConsoleLogs") === "true") {
      console.info(
        `%c${target.toUpperCase()}`,
        `padding: 0 5px; background-color: ${color}; color: white;`,
        ev,
        props,
      ) // eslint-disable-line no-console
    }
  }
}

export default new Cuckoo()
